import React, { useState, useEffect } from 'react';
import { Container, Modal, Button, Accordion, Form, Badge, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getFaqsApi } from '../../api/faq';
import { addEnquiryApi, getEnquiryAnswersApi, markEnquiryReadApi } from '../../api/enquiry';
import {
  createRefundRequestApi,
  getMyRefundRequestsApi,
  getRefundRequestByIdApi,
  getTeacherRefundRequestsApi,
  approveRefundRequestApi,
  rejectRefundRequestApi,
  getTeacherRefundsDoneApi,
} from '../../api/refunds';
import {
  addBankDetailsApi,
  getBankDetailsApi,
  getBankDetailsByIdApi,
  updateBankDetailsApi,
  deleteBankAccountApi,
} from '../../api/bank';
import {
  getClassesRecordingsApi,
  addClassRecordingApi,
  updateRecordingApi,
  deleteRecordingApi,
} from '../../api/recordings';
import { getSavedPostsApi, toggleSavePostApi, getPracticeHistoryApi, getPaymentHistoryApi } from '../../api/profile';
import './SettingsPage.scss';

const DEFAULT_FAQS = [
  {
    _id: '1',
    question: 'How do 1-on-1 speaking classes work?',
    answer: 'You can browse verified teachers, pick a convenient time slot, and book a live video session directly within YooSpeak.',
  },
  {
    _id: '2',
    question: 'What is the AI Speaking Coach?',
    answer: 'The AI Speaking Coach provides instant feedback on pronunciation, fluency, vocabulary, and grammar score after every practice session.',
  },
  {
    _id: '3',
    question: 'How can I withdraw teacher earnings?',
    answer: 'Teacher earnings are processed automatically to your registered bank account or PayPal/UPI ID on a weekly schedule.',
  },
  {
    _id: '4',
    question: 'Can I switch plans or cancel anytime?',
    answer: 'Yes, you can upgrade, downgrade, or cancel your subscription at any time directly from your settings or profile.',
  },
  {
    _id: '5',
    question: 'How do community audio/video feeds work?',
    answer: 'You can join interactive language communities, upload short video responses, practice with peers, and get community feedback.',
  },
];

export const SettingsPage = () => {
  const navigate = useNavigate();

  // Active modal state
  const [activeModal, setActiveModal] = useState(null); // 'recordings' | 'saved' | 'practice' | 'payments' | 'bank' | 'faq' | 'support' | 'share' | null
  const [copySuccess, setCopySuccess] = useState(false);
  const [supportMessageSent, setSupportMessageSent] = useState(false);

  // FAQ state
  const [faqs, setFaqs] = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);

  useEffect(() => {
    if (activeModal === 'faq' && faqs.length === 0) {
      setFaqLoading(true);
      getFaqsApi()
        .then((res) => {
          let list = [];
          if (Array.isArray(res)) {
            list = res;
          } else if (res && Array.isArray(res.data)) {
            list = res.data;
          } else if (res && Array.isArray(res.faqs)) {
            list = res.faqs;
          } else if (res && Array.isArray(res.result)) {
            list = res.result;
          }

          if (list.length > 0) {
            setFaqs(list);
          } else {
            setFaqs(DEFAULT_FAQS);
          }
        })
        .catch((err) => {
          console.error('Failed to load FAQs API, using default FAQs:', err);
          setFaqs(DEFAULT_FAQS);
        })
        .finally(() => {
          setFaqLoading(false);
        });
    }
  }, [activeModal, faqs.length]);

  // Bank accounts state & handlers
  const [bankList, setBankList] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankSubmitting, setBankSubmitting] = useState(false);
  const [payoutType, setPayoutType] = useState('bank'); // 'bank' | 'paypal'
  const [bankForm, setBankForm] = useState({
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    routing_number: '',
    swift_code: '',
  });

  const fetchBankAccounts = () => {
    setBankLoading(true);
    getBankDetailsApi()
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        setBankList(list);
      })
      .catch((err) => console.error('Failed to fetch bank details:', err))
      .finally(() => setBankLoading(false));
  };

  const handleEditBankSelect = (id) => {
    setSelectedBankId(id);
    setBankLoading(true);
    getBankDetailsByIdApi(id)
      .then((res) => {
        const item = res?.data || res;
        if (item) {
          setBankForm({
            account_holder_name: item.account_holder_name || '',
            bank_name: item.bank_name || '',
            account_number: item.account_number || '',
            routing_number: item.routing_number || '',
            swift_code: item.swift_code || '',
          });
          if (item.routing_number === 'PAYPAL') {
            setPayoutType('paypal');
          } else {
            setPayoutType('bank');
          }
        }
      })
      .catch((err) => console.error('Failed to fetch bank detail by ID:', err))
      .finally(() => setBankLoading(false));
  };

  const handleSaveBankForm = (e) => {
    e.preventDefault();
    setBankSubmitting(true);

    const submissionForm = payoutType === 'paypal' ? {
      account_holder_name: bankForm.account_holder_name,
      bank_name: 'PayPal',
      account_number: bankForm.account_number,
      routing_number: 'PAYPAL',
      swift_code: '',
    } : {
      account_holder_name: bankForm.account_holder_name,
      bank_name: bankForm.bank_name || 'Bank Account',
      account_number: bankForm.account_number,
      routing_number: bankForm.routing_number,
      swift_code: bankForm.swift_code,
    };

    const promise = selectedBankId
      ? updateBankDetailsApi(selectedBankId, submissionForm)
      : addBankDetailsApi(submissionForm);

    promise
      .then(() => {
        alert(selectedBankId ? 'Payout details updated!' : 'Payout details saved!');
        setSelectedBankId(null);
        setBankForm({ account_holder_name: '', bank_name: '', account_number: '', routing_number: '', swift_code: '' });
        fetchBankAccounts();
      })
      .catch((err) => {
        console.error('Failed to save bank details:', err);
        alert(err?.response?.data?.message || 'Failed to save bank details');
      })
      .finally(() => setBankSubmitting(false));
  };

  const handleDeleteBank = (id) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    setBankLoading(true);
    deleteBankAccountApi(id)
      .then(() => {
        alert('Bank account deleted');
        fetchBankAccounts();
      })
      .catch((err) => {
        console.error('Failed to delete bank account:', err);
        alert(err?.response?.data?.message || 'Failed to delete bank account');
      })
      .finally(() => setBankLoading(false));
  };

  // Class Recordings state & handlers
  const [recordingsList, setRecordingsList] = useState([]);
  const [recordingsLoading, setRecordingsLoading] = useState(false);
  const [recordingSubmitting, setRecordingSubmitting] = useState(false);
  const [newRecordingForm, setNewRecordingForm] = useState({ classId: '', recording_url: '' });

  const fetchRecordings = () => {
    setRecordingsLoading(true);
    getClassesRecordingsApi()
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        setRecordingsList(list);
      })
      .catch((err) => console.error('Failed to fetch class recordings:', err))
      .finally(() => setRecordingsLoading(false));
  };

  const handleAddRecordingSubmit = (e) => {
    e.preventDefault();
    if (!newRecordingForm.classId || !newRecordingForm.recording_url) {
      alert('Class ID and Recording URL are required');
      return;
    }
    setRecordingSubmitting(true);
    addClassRecordingApi(newRecordingForm.classId, { recording_url: newRecordingForm.recording_url })
      .then(() => {
        alert('Recording added successfully!');
        setNewRecordingForm({ classId: '', recording_url: '' });
        fetchRecordings();
      })
      .catch((err) => {
        console.error('Failed to add recording:', err);
        alert(err?.response?.data?.message || 'Failed to add recording');
      })
      .finally(() => setRecordingSubmitting(false));
  };

  const handleUpdateRecordingItem = (id) => {
    const url = prompt('Enter new Recording URL:');
    if (!url) return;
    setRecordingsLoading(true);
    updateRecordingApi(id, { recording_url: url })
      .then(() => {
        alert('Recording updated');
        fetchRecordings();
      })
      .catch((err) => alert(err?.response?.data?.message || 'Failed to update recording'))
      .finally(() => setRecordingsLoading(false));
  };

  const handleDeleteRecordingItem = (id) => {
    if (!window.confirm('Delete this recording?')) return;
    setRecordingsLoading(true);
    deleteRecordingApi(id)
      .then(() => {
        alert('Recording deleted');
        fetchRecordings();
      })
      .catch((err) => alert(err?.response?.data?.message || 'Failed to delete recording'))
      .finally(() => setRecordingsLoading(false));
  };
  // Enquiry / Support state
  const [enquirySubject, setEnquirySubject] = useState('');
  const [enquiryMessage, setEnquiryMessage] = useState('');
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [enquiriesList, setEnquiriesList] = useState([]);
  const [enquiryLoading, setEnquiryLoading] = useState(false);

  useEffect(() => {
    if (activeModal === 'support') {
      setEnquiryLoading(true);
      getEnquiryAnswersApi()
        .then((res) => {
          let list = [];
          if (Array.isArray(res)) list = res;
          else if (res && Array.isArray(res.data)) list = res.data;
          else if (res && Array.isArray(res.enquiries)) list = res.enquiries;
          else if (res && Array.isArray(res.result)) list = res.result;
          setEnquiriesList(list);
        })
        .catch((err) => console.error('Failed to load enquiry answers API:', err))
        .finally(() => setEnquiryLoading(false));
    } else if (activeModal === 'bank') {
      fetchBankAccounts();
    } else if (activeModal === 'recordings') {
      fetchRecordings();
    } else if (activeModal === 'saved') {
      fetchSavedPosts();
    } else if (activeModal === 'practice') {
      fetchPracticeHistory();
    } else if (activeModal === 'payments') {
      fetchPaymentHistory();
    }
  }, [activeModal]);

  const closeModal = () => {
    setActiveModal(null);
    setCopySuccess(false);
    setSupportMessageSent(false);
  };

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText('https://yoospeak.com/app?ref=SHARE2026');
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 3000);
  };

  const handleSupportSubmit = (e) => {
    e.preventDefault();
    const questionText = [enquirySubject, enquiryMessage].filter(Boolean).join('\n');
    setEnquirySubmitting(true);
    addEnquiryApi({ question: questionText })
      .then(() => {
        setSupportMessageSent(true);
        setEnquirySubject('');
        setEnquiryMessage('');
        getEnquiryAnswersApi()
          .then((res) => {
            let list = [];
            if (Array.isArray(res)) list = res;
            else if (res && Array.isArray(res.data)) list = res.data;
            else if (res && Array.isArray(res.enquiries)) list = res.enquiries;
            setEnquiriesList(list);
          })
          .catch(() => { });
      })
      .catch((err) => {
        console.error('Failed to submit enquiry API:', err);
        setSupportMessageSent(true);
      })
      .finally(() => {
        setEnquirySubmitting(false);
      });
  };

  const handleMarkEnquiryRead = (id) => {
    markEnquiryReadApi(id)
      .then(() => {
        setEnquiriesList((prev) =>
          prev.map((item) => (item._id === id || item.id === id ? { ...item, isRead: true, read: true } : item))
        );
      })
      .catch((err) => console.error('Failed to mark enquiry read API:', err));
  };

  // Refund state
  const [refundTab, setRefundTab] = useState('my-requests'); // 'my-requests' | 'create' | 'teacher-incoming' | 'teacher-done'
  const [myRefunds, setMyRefunds] = useState([]);
  const [teacherRefunds, setTeacherRefunds] = useState([]);
  const [teacherDoneRefunds, setTeacherDoneRefunds] = useState([]);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundSuccess, setRefundSuccess] = useState(false);
  const [refundForm, setRefundForm] = useState({ transaction_id: '', amount: '', reason: '' });

  // Saved Posts state
  const [savedPosts, setSavedPosts] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);

  const fetchSavedPosts = () => {
    setSavedLoading(true);
    getSavedPostsApi()
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        setSavedPosts(list);
      })
      .catch((err) => console.error('Failed to fetch saved posts:', err))
      .finally(() => setSavedLoading(false));
  };

  const handleUnsavePost = (postId) => {
    if (!window.confirm('Are you sure you want to remove this saved item?')) return;
    toggleSavePostApi(postId)
      .then(() => {
        setSavedPosts((prev) => prev.filter((item) => item._id !== postId));
      })
      .catch((err) => {
        console.error('Failed to unsave post:', err);
        alert(err?.response?.data?.message || 'Failed to remove saved item');
      });
  };

  // Practice History state
  const [practiceHistory, setPracticeHistory] = useState([]);
  const [practiceLoading, setPracticeLoading] = useState(false);

  const fetchPracticeHistory = () => {
    setPracticeLoading(true);
    getPracticeHistoryApi(1, 20)
      .then((res) => {
        const list = res?.data || [];
        setPracticeHistory(list);
      })
      .catch((err) => console.error('Failed to fetch practice history:', err))
      .finally(() => setPracticeLoading(false));
  };

  // Payment History state
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const fetchPaymentHistory = () => {
    setPaymentLoading(true);
    getPaymentHistoryApi()
      .then((res) => {
        const list = res?.data || [];
        setPaymentHistory(list);
      })
      .catch((err) => console.error('Failed to fetch payment history:', err))
      .finally(() => setPaymentLoading(false));
  };

  const loadRefundsData = () => {
    setRefundLoading(true);
    if (refundTab === 'my-requests' || refundTab === 'create') {
      getMyRefundRequestsApi()
        .then((res) => setMyRefunds(Array.isArray(res) ? res : res?.data || []))
        .catch((err) => console.error('Failed to load my refund requests:', err))
        .finally(() => setRefundLoading(false));
    } else if (refundTab === 'teacher-incoming') {
      getTeacherRefundRequestsApi()
        .then((res) => setTeacherRefunds(Array.isArray(res) ? res : res?.data || []))
        .catch((err) => console.error('Failed to load teacher refund requests:', err))
        .finally(() => setRefundLoading(false));
    } else if (refundTab === 'teacher-done') {
      getTeacherRefundsDoneApi()
        .then((res) => setTeacherDoneRefunds(Array.isArray(res) ? res : res?.data || []))
        .catch((err) => console.error('Failed to load teacher refunds done:', err))
        .finally(() => setRefundLoading(false));
    }
  };

  useEffect(() => {
    if (activeModal === 'refunds') {
      loadRefundsData();
    }
  }, [activeModal, refundTab]);

  const handleCreateRefundSubmit = (e) => {
    e.preventDefault();
    setRefundLoading(true);
    createRefundRequestApi({
      transaction_id: refundForm.transaction_id,
      amount: Number(refundForm.amount),
      reason: refundForm.reason,
    })
      .then(() => {
        setRefundSuccess(true);
        setRefundForm({ transaction_id: '', amount: '', reason: '' });
        setRefundTab('my-requests');
        loadRefundsData();
      })
      .catch((err) => {
        console.error('Failed to submit refund request:', err);
        alert(err?.response?.data?.message || 'Failed to submit refund request');
      })
      .finally(() => setRefundLoading(false));
  };

  const handleApproveRefund = (id) => {
    setRefundLoading(true);
    approveRefundRequestApi(id)
      .then(() => {
        alert('Refund approved successfully!');
        loadRefundsData();
      })
      .catch((err) => {
        console.error('Failed to approve refund:', err);
        alert(err?.response?.data?.message || 'Failed to approve refund');
      })
      .finally(() => setRefundLoading(false));
  };

  const handleRejectRefund = (id) => {
    const notes = prompt('Reason for rejecting refund:');
    if (notes === null) return;
    setRefundLoading(true);
    rejectRefundRequestApi(id, notes)
      .then(() => {
        alert('Refund rejected');
        loadRefundsData();
      })
      .catch((err) => {
        console.error('Failed to reject refund:', err);
        alert(err?.response?.data?.message || 'Failed to reject refund');
      })
      .finally(() => setRefundLoading(false));
  };

  // Group 1 items
  const group1 = [
    {
      id: 'recordings',
      label: 'Recordings',
      icon: 'bi-headphones',
      badgeClass: 'badge-purple',
      onClick: () => setActiveModal('recordings'),
    },
    {
      id: 'saved',
      label: 'Saved',
      icon: 'bi-bookmark-fill',
      badgeClass: 'badge-purple',
      onClick: () => setActiveModal('saved'),
    },
  ];

  // Group 2 items
  const group2 = [
    {
      id: 'practice',
      label: 'Practice History',
      icon: 'bi-clock-history',
      badgeClass: 'badge-yellow',
      onClick: () => setActiveModal('practice'),
    },
    {
      id: 'payments',
      label: 'Payments History',
      icon: 'bi-receipt',
      badgeClass: 'badge-purple',
      onClick: () => setActiveModal('payments'),
    },
    {
      id: 'refunds',
      label: 'Refund Requests',
      icon: 'bi-arrow-counterclockwise',
      badgeClass: 'badge-red',
      onClick: () => setActiveModal('refunds'),
    },
    {
      id: 'earnings',
      label: 'Earnings',
      icon: 'bi-currency-dollar',
      badgeClass: 'badge-green',
      onClick: () => navigate('/revenue'),
    },
    {
      id: 'bank',
      label: 'Bank Accounts',
      icon: 'bi-credit-card-2-front',
      badgeClass: 'badge-yellow',
      onClick: () => setActiveModal('bank'),
    },
  ];

  // Group 3 items
  const group3 = [
    {
      id: 'faq',
      label: "FAQ's",
      icon: 'bi-chat-left-text-fill',
      badgeClass: 'badge-purple',
      onClick: () => setActiveModal('faq'),
    },
    {
      id: 'support',
      label: 'Help & Support',
      icon: 'bi-headset',
      badgeClass: 'badge-green',
      onClick: () => setActiveModal('support'),
    },
    {
      id: 'terms',
      label: 'Terms & Conditions',
      icon: 'bi-file-earmark-text-fill',
      badgeClass: 'badge-yellow',
      onClick: () => navigate('/terms-of-service'),
    },
    {
      id: 'privacy',
      label: 'Privacy Policy',
      icon: 'bi-shield-check',
      badgeClass: 'badge-red',
      onClick: () => navigate('/privacy-policy'),
    },
    {
      id: 'share',
      label: 'Share YooSpeak',
      icon: 'bi-share-fill',
      badgeClass: 'badge-purple',
      onClick: () => setActiveModal('share'),
    },
  ];

  const renderGroupCard = (items) => (
    <div className="settings-group-card">
      {items.map((item) => (
        <div
          key={item.id}
          className="settings-item"
          onClick={item.onClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && item.onClick()}
        >
          <div className="item-left">
            <div className={`icon-badge ${item.badgeClass}`}>
              <i className={`bi ${item.icon}`}></i>
            </div>
            <span className="item-label">{item.label}</span>
          </div>
          <i className="bi bi-chevron-right item-chevron"></i>
        </div>
      ))}
    </div>
  );

  return (
    <div className="settings-page">
      <Container className="settings-container">
        {/* Header */}
        <div className="settings-header text-center text-md-start">
          <h1 className="settings-title">Settings</h1>
          <p className="settings-subtitle">
            Manage your practice history, recordings, payments, and app preferences.
          </p>
        </div>

        {/* Grouped Section Cards */}
        {renderGroupCard(group1)}
        {renderGroupCard(group2)}
        {renderGroupCard(group3)}
      </Container>

      {/* ── MODALS ── */}

      {/* Recordings Modal */}
      <Modal show={activeModal === 'recordings'} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title><i className="bi bi-headphones text-primary me-2"></i>Class Recordings</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {/* Add Recording Form */}
          <Form onSubmit={handleAddRecordingSubmit} className="p-3 border rounded-3 bg-light mb-4">
            <h6 className="fw-bold text-dark mb-3"><i className="bi bi-plus-circle me-2 text-primary"></i>Add Class Recording</h6>
            <div className="row g-2">
              <div className="col-md-5">
                <Form.Control
                  required
                  placeholder="Class ID (e.g. 64f1b2...)"
                  value={newRecordingForm.classId}
                  onChange={(e) => setNewRecordingForm({ ...newRecordingForm, classId: e.target.value })}
                />
              </div>
              <div className="col-md-5">
                <Form.Control
                  required
                  placeholder="Recording URL (https://...)"
                  value={newRecordingForm.recording_url}
                  onChange={(e) => setNewRecordingForm({ ...newRecordingForm, recording_url: e.target.value })}
                />
              </div>
              <div className="col-md-2">
                <Button type="submit" variant="primary" className="w-100 fw-bold" disabled={recordingSubmitting}>
                  {recordingSubmitting ? <Spinner animation="border" size="sm" /> : 'Add'}
                </Button>
              </div>
            </div>
          </Form>

          {/* Recordings List */}
          <h6 className="fw-bold text-dark mb-3"><i className="bi bi-film me-2 text-primary"></i>Your Class Recordings</h6>
          {recordingsLoading ? (
            <div className="text-center py-4"><Spinner animation="border" variant="primary" size="sm" /></div>
          ) : recordingsList.length === 0 ? (
            <div className="text-center py-4 bg-light rounded-3 text-muted">
              <i className="bi bi-camera-video-off fs-2 d-block mb-1"></i>
              <span>No recordings available for your classes.</span>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {recordingsList.map((rec, index) => {
                const recId = rec._id || rec.id || index;
                const recUrl = rec.recording_url || rec.url || '#';
                const classTitle = rec.class?.title || rec.title || 'Class Recording';
                return (
                  <div key={recId} className="p-3 border rounded-3 d-flex align-items-center justify-content-between bg-white shadow-sm">
                    <div className="d-flex align-items-center gap-3">
                      <div className="bg-primary bg-opacity-10 text-primary rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                        <i className="bi bi-play-circle-fill fs-5"></i>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold text-dark">{classTitle}</h6>
                        <small className="text-muted d-block text-truncate" style={{ maxWidth: '300px' }}>{recUrl}</small>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <a href={recUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm rounded-pill">
                        <i className="bi bi-play-fill me-1"></i>Watch
                      </a>
                      <Button variant="outline-warning" size="sm" className="rounded-pill" onClick={() => handleUpdateRecordingItem(recId)}>
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button variant="outline-danger" size="sm" className="rounded-pill" onClick={() => handleDeleteRecordingItem(recId)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Saved Items Modal */}
      <Modal show={activeModal === 'saved'} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title><i className="bi bi-bookmark-fill text-primary me-2"></i>Saved Items</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {savedLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : savedPosts.length === 0 ? (
            <div className="text-center py-5 bg-light rounded-4 text-muted border-dashed">
              <i className="bi bi-bookmark-dash fs-1 mb-2 d-block"></i>
              <h5>No saved items yet</h5>
              <p className="mb-0 small">Posts you bookmark in the video feed will appear here.</p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {savedPosts.map((saved) => (
                <div key={saved._id} className="p-3 border rounded-3 d-flex align-items-center justify-content-between bg-white shadow-sm">
                  <div className="d-flex align-items-center gap-3">
                    <img
                      src={saved.thumbnailUrl || saved.teacher?.profile_picture || 'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=80&h=80&fit=crop'}
                      alt={saved.title}
                      className="rounded object-fit-cover"
                      style={{ width: '60px', height: '60px', border: '1px solid #e2e8f0' }}
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=80&h=80&fit=crop'; }}
                    />
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <Badge bg="primary" className="text-xs">Video Feed</Badge>
                        {saved.teacher?.full_name && (
                          <span className="text-xs text-gray-500 fw-medium">by {saved.teacher.full_name}</span>
                        )}
                      </div>
                      <h6 className="fw-bold text-dark mb-0">{saved.title || 'Untitled Post'}</h6>
                      <small className="text-muted text-xs">Saved on {new Date(saved.createdAt).toLocaleDateString()}</small>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" className="rounded-pill px-3" onClick={() => { closeModal(); navigate('/video-feed'); }}>
                      <i className="bi bi-play-fill me-1"></i>View
                    </Button>
                    <Button variant="outline-danger" size="sm" className="rounded-circle" onClick={() => handleUnsavePost(saved._id)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Practice History Modal */}
      <Modal show={activeModal === 'practice'} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title><i className="bi bi-clock-history text-warning me-2"></i>Practice History</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {practiceLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="warning" />
            </div>
          ) : practiceHistory.length === 0 ? (
            <div className="text-center py-5 bg-light rounded-4 text-muted border-dashed">
              <i className="bi bi-calendar-x fs-1 mb-2 d-block"></i>
              <h5>No practice history found</h5>
              <p className="mb-0 small">Matches you accept and practice in the app will appear here.</p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {practiceHistory.map((item) => {
                const partnerName = item.matchedUser?.full_name || 'Anonymous Peer';
                const partnerPic = item.matchedUser?.profile_picture || 'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=80&h=80&fit=crop';
                const flag = item.language?.flag || '🌐';
                const langName = item.language?.name || 'Language Practice';

                return (
                  <div key={item.matchId} className="p-3 border rounded-3 d-flex align-items-center justify-content-between bg-white shadow-sm">
                    <div className="d-flex align-items-center gap-3">
                      <img
                        src={partnerPic}
                        alt={partnerName}
                        className="rounded-circle object-fit-cover"
                        style={{ width: '50px', height: '50px', border: '1px solid #e2e8f0' }}
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=80&h=80&fit=crop'; }}
                      />
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <Badge bg={item.matchedUser?.user_type === 'teacher' ? 'success' : 'secondary'} className="text-xs">
                            {item.matchedUser?.user_type === 'teacher' ? 'Instructor' : 'Peer'}
                          </Badge>
                          <span className="text-xs text-gray-500 fw-medium">
                            {flag} {langName}
                          </span>
                        </div>
                        <h6 className="fw-bold text-dark mb-0">Practiced with {partnerName}</h6>
                        <small className="text-muted text-xs">
                          {new Date(item.createdAt).toLocaleString()}
                        </small>
                      </div>
                    </div>
                    <Badge bg={item.status === 'accepted' ? 'success' : 'secondary'} className="px-3 py-2">
                      {item.status === 'accepted' ? 'Completed' : 'Skipped'}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Payments History Modal */}
      <Modal show={activeModal === 'payments'} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title><i className="bi bi-receipt text-primary me-2"></i>Payments History</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {paymentLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : paymentHistory.length === 0 ? (
            <div className="text-center py-5 bg-light rounded-4 text-muted border-dashed">
              <i className="bi bi-credit-card fs-1 mb-2 d-block"></i>
              <h5>No payment history found</h5>
              <p className="mb-0 small">Your transaction receipts and subscriptions will appear here.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((txn) => {
                    let desc = 'YooSpeak Service Payment';
                    if (txn.community?.name) desc = `Community: ${txn.community.name}`;
                    else if (txn.event?.title) desc = `Event: ${txn.event.title}`;
                    else if (txn.course?.title) desc = `Course: ${txn.course.title}`;
                    else if (txn.class?.title) desc = `Class: ${txn.class.title}`;
                    else if (txn.blue_verification_id) desc = `Blue Verification Subscription`;
                    else if (txn.subscription_plan?.name) desc = `Subscription: ${txn.subscription_plan.name}`;
                    else if (txn.transaction_type === 'subscription') desc = `Plan Subscription`;

                    const dateStr = new Date(txn.createdAt).toLocaleDateString();
                    const amountVal = (txn.amount || 0).toFixed(2);
                    const isSuccess = txn.status === 'succeeded' || txn.status === 'completed' || txn.status === 'active';
                    const isPending = txn.status === 'pending';
                    
                    const receiptUrl = txn.receipt_url || txn.stripe_details?.receipt_url || null;

                    return (
                      <tr key={txn._id}>
                        <td className="fw-medium text-dark">{desc}</td>
                        <td>{dateStr}</td>
                        <td className="fw-bold">${amountVal}</td>
                        <td>
                          {isSuccess && <Badge bg="success">Completed</Badge>}
                          {isPending && <Badge bg="warning" text="dark">Pending</Badge>}
                          {!isSuccess && !isPending && <Badge bg="danger">Failed</Badge>}
                        </td>
                        <td>
                          {receiptUrl ? (
                            <a href={receiptUrl} target="_blank" rel="noreferrer" className="btn btn-link btn-sm p-0">
                              <i className="bi bi-receipt me-1"></i>Receipt
                            </a>
                          ) : (
                            <span className="text-muted text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Bank Accounts Modal */}
      <Modal show={activeModal === 'bank'} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title><i className="bi bi-bank text-warning me-2"></i>Bank Accounts & Payouts</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {/* Bank Accounts List */}
          <h6 className="fw-bold text-dark mb-3"><i className="bi bi-credit-card-2-front me-2 text-primary"></i>Saved Bank Accounts</h6>
          {bankLoading ? (
            <div className="text-center py-4"><Spinner animation="border" variant="warning" size="sm" /></div>
          ) : bankList.length === 0 ? (
            <div className="text-center py-3 bg-light rounded-3 text-muted mb-4">
              <small>No bank accounts added yet.</small>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3 mb-4">
              {bankList.map((b) => {
                const bId = b._id || b.id;
                const isPaypal = b.routing_number === 'PAYPAL';
                return (
                  <div key={bId} className="p-3 border rounded-3 bg-white shadow-sm d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <div className={`p-2 rounded-3 ${isPaypal ? 'bg-primary bg-opacity-10 text-primary' : 'bg-warning bg-opacity-10 text-warning'}`} style={{ width: 44, height: 44, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <i className={`bi ${isPaypal ? 'bi-paypal fs-5' : 'bi-bank fs-5'}`}></i>
                      </div>
                      <div>
                        <div className="fw-bold text-dark">{isPaypal ? 'PayPal Account' : (b.bank_name || 'Bank Account')}</div>
                        <small className="text-muted">
                          {isPaypal ? `Email: ${b.account_number}` : `Holder: ${b.account_holder_name} · Acc: ${b.account_number}`}
                        </small>
                        {!isPaypal && b.routing_number && (
                          <div className="text-xs text-muted">Routing/IFSC: {b.routing_number}</div>
                        )}
                        {isPaypal && (
                          <div className="text-xs text-muted">Holder: {b.account_holder_name}</div>
                        )}
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button variant="outline-warning" size="sm" className="rounded-pill px-3" onClick={() => handleEditBankSelect(bId)}>
                        <i className="bi bi-pencil me-1"></i>Edit
                      </Button>
                      <Button variant="outline-danger" size="sm" className="rounded-pill" onClick={() => handleDeleteBank(bId)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Payout Type Selector */}
          <div className="d-flex gap-2 mb-4">
            <Button
              variant={payoutType === 'bank' ? 'warning text-dark' : 'outline-secondary'}
              className="w-50 rounded-pill py-2 fw-bold"
              onClick={() => {
                setPayoutType('bank');
                if (!selectedBankId) {
                  setBankForm({ account_holder_name: '', bank_name: '', account_number: '', routing_number: '', swift_code: '' });
                }
              }}
            >
              <i className="bi bi-bank me-2"></i>Bank Account
            </Button>
            <Button
              variant={payoutType === 'paypal' ? 'primary text-white' : 'outline-secondary'}
              className="w-50 rounded-pill py-2 fw-bold"
              onClick={() => {
                setPayoutType('paypal');
                if (!selectedBankId) {
                  setBankForm({ account_holder_name: '', bank_name: 'PayPal', account_number: '', routing_number: 'PAYPAL', swift_code: '' });
                }
              }}
            >
              <i className="bi bi-paypal me-2"></i>PayPal
            </Button>
          </div>

          {/* Add / Edit Bank Form */}
          <Form onSubmit={handleSaveBankForm} className="p-3 border rounded-3 bg-light">
            <h6 className="fw-bold text-dark mb-3">
              {selectedBankId
                ? `Edit ${payoutType === 'paypal' ? 'PayPal' : 'Bank Account'} Details`
                : `Add New ${payoutType === 'paypal' ? 'PayPal Payout' : 'Bank Account'}`}
            </h6>

            {payoutType === 'paypal' ? (
              // PayPal Fields
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold">PayPal Email Address *</Form.Label>
                    <Form.Control
                      required
                      type="email"
                      placeholder="e.g. yourname@paypal.com"
                      value={bankForm.account_number}
                      onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold">Account Holder Name *</Form.Label>
                    <Form.Control
                      required
                      placeholder="Account Holder Full Name"
                      value={bankForm.account_holder_name}
                      onChange={(e) => setBankForm({ ...bankForm, account_holder_name: e.target.value })}
                    />
                  </Form.Group>
                </div>
              </div>
            ) : (
              // Bank Fields
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold">Account Holder Name *</Form.Label>
                    <Form.Control
                      required
                      placeholder="Full Name as on Bank Account"
                      value={bankForm.account_holder_name}
                      onChange={(e) => setBankForm({ ...bankForm, account_holder_name: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold">Bank Name</Form.Label>
                    <Form.Control
                      placeholder="e.g. Chase / HDFC / HSBC"
                      value={bankForm.bank_name}
                      onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold">Account Number *</Form.Label>
                    <Form.Control
                      required
                      placeholder="Account Number"
                      value={bankForm.account_number}
                      onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold">Routing Number / IFSC *</Form.Label>
                    <Form.Control
                      required
                      placeholder="Routing / IFSC Code"
                      value={bankForm.routing_number}
                      onChange={(e) => setBankForm({ ...bankForm, routing_number: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-12">
                  <Form.Group>
                    <Form.Label className="fw-semibold">SWIFT / BIC Code (Optional)</Form.Label>
                    <Form.Control
                      placeholder="SWIFT / BIC Code for international transfers"
                      value={bankForm.swift_code}
                      onChange={(e) => setBankForm({ ...bankForm, swift_code: e.target.value })}
                    />
                  </Form.Group>
                </div>
              </div>
            )}

            <div className="d-flex gap-2 mt-3">
              <Button variant="warning" type="submit" className="w-100 rounded-pill py-2 fw-bold text-dark" disabled={bankSubmitting}>
                {bankSubmitting ? <Spinner animation="border" size="sm" /> : selectedBankId ? 'Update Payout Account' : 'Save Payout Account'}
              </Button>
              {selectedBankId && (
                <Button variant="outline-secondary" className="rounded-pill px-4" onClick={() => { setSelectedBankId(null); setBankForm({ account_holder_name: '', bank_name: '', account_number: '', routing_number: '', swift_code: '' }); }}>
                  Cancel
                </Button>
              )}
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* FAQs Modal */}
      <Modal show={activeModal === 'faq'} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title><i className="bi bi-chat-left-text-fill text-primary me-2"></i>Frequently Asked Questions</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {faqLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted text-sm mt-2">Loading FAQs...</p>
            </div>
          ) : (
            <Accordion defaultActiveKey="0">
              {faqs.map((faq, index) => {
                const questionText = faq.question || faq.title || faq.q || 'Question';
                const answerText = faq.answer || faq.description || faq.content || faq.a || 'Answer';
                return (
                  <Accordion.Item key={faq._id || index} eventKey={String(index)}>
                    <Accordion.Header className="fw-semibold">{questionText}</Accordion.Header>
                    <Accordion.Body className="text-gray-700">{answerText}</Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Modal.Body>
      </Modal>

      {/* Help & Support / Enquiry Modal */}
      <Modal show={activeModal === 'support'} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title><i className="bi bi-headset text-success me-2"></i>Help & Support (Enquiry)</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {supportMessageSent && (
            <Alert variant="success" className="rounded-3 d-flex align-items-center justify-content-between mb-4" onClose={() => setSupportMessageSent(false)} dismissible>
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-check-circle-fill fs-5"></i>
                <span>Your enquiry has been submitted successfully!</span>
              </div>
            </Alert>
          )}

          <Form onSubmit={handleSupportSubmit} className="mb-4 p-3 border rounded-3 bg-light">
            <h6 className="fw-bold text-dark mb-3"><i className="bi bi-pencil-square me-2 text-primary"></i>Submit New Enquiry</h6>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Subject</Form.Label>
              <Form.Control
                required
                value={enquirySubject}
                onChange={(e) => setEnquirySubject(e.target.value)}
                placeholder="e.g. Question about class booking or earnings"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">How can we help you?</Form.Label>
              <Form.Control
                required
                as="textarea"
                rows={3}
                value={enquiryMessage}
                onChange={(e) => setEnquiryMessage(e.target.value)}
                placeholder="Describe your issue or feedback in detail..."
              />
            </Form.Group>
            <Button type="submit" variant="success" className="w-100 rounded-pill py-2 fw-bold" disabled={enquirySubmitting}>
              {enquirySubmitting ? (
                <><Spinner animation="border" size="sm" className="me-2" />Submitting...</>
              ) : (
                <><i className="bi bi-send-fill me-2"></i>Submit Enquiry</>
              )}
            </Button>
          </Form>

          {/* Enquiries & Answers Section */}
          <div className="enquiries-section">
            <h6 className="fw-bold text-dark mb-3"><i className="bi bi-chat-left-dots-fill me-2 text-info"></i>Your Support Enquiries & Answers</h6>
            {enquiryLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" variant="primary" />
              </div>
            ) : enquiriesList.length === 0 ? (
              <div className="text-center py-4 text-muted bg-white border rounded-3">
                <i className="bi bi-inbox fs-3 d-block mb-1 opacity-50"></i>
                <small>No support enquiries submitted yet.</small>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {enquiriesList.map((enq, i) => {
                  const id = enq._id || enq.id || i;
                  const isRead = enq.isRead || enq.read || enq.isReadByUser;
                  const questionText = enq.question || enq.subject || enq.message || 'Enquiry';
                  return (
                    <div key={id} className={`p-3 border rounded-3 bg-white shadow-sm ${!isRead ? 'border-primary border-2' : ''}`}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-bold mb-0 text-dark">{questionText}</h6>
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg={enq.answer || enq.response ? 'success' : 'warning'}>
                            {enq.answer || enq.response ? 'Answered' : 'Pending'}
                          </Badge>
                          {!isRead && (
                            <Button variant="outline-primary" size="sm" className="py-0 px-2 text-xs rounded-pill" onClick={() => handleMarkEnquiryRead(id)}>
                              Mark Read
                            </Button>
                          )}
                        </div>
                      </div>
                      {(enq.answer || enq.response) && (
                        <div className="bg-light p-2 rounded-2 border-start border-4 border-success text-sm">
                          <strong>Response:</strong> {enq.answer || enq.response}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* Refund Requests Modal */}
      <Modal show={activeModal === 'refunds'} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title><i className="bi bi-arrow-counterclockwise text-danger me-2"></i>Refund Requests Management</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {/* Sub Nav Tabs */}
          <div className="d-flex flex-wrap gap-2 mb-4 border-bottom pb-3">
            <Button
              variant={refundTab === 'my-requests' ? 'danger' : 'outline-secondary'}
              size="sm"
              className="rounded-pill px-3"
              onClick={() => setRefundTab('my-requests')}
            >
              My Requests
            </Button>
            {/* <Button
              variant={refundTab === 'create' ? 'danger' : 'outline-secondary'}
              size="sm"
              className="rounded-pill px-3"
              onClick={() => setRefundTab('create')}
            >
              + Submit Refund
            </Button>
            <Button
              variant={refundTab === 'teacher-incoming' ? 'danger' : 'outline-secondary'}
              size="sm"
              className="rounded-pill px-3"
              onClick={() => setRefundTab('teacher-incoming')}
            >
              Teacher: Incoming Requests
            </Button>
            <Button
              variant={refundTab === 'teacher-done' ? 'danger' : 'outline-secondary'}
              size="sm"
              className="rounded-pill px-3"
              onClick={() => setRefundTab('teacher-done')}
            >
              Teacher: Refunds History
            </Button> */}
          </div>

          {/* Tab 1: My Requests */}
          {refundTab === 'my-requests' && (
            <div>
              <h6 className="fw-bold text-dark mb-3">My Refund Requests</h6>
              {refundLoading ? (
                <div className="text-center py-4"><Spinner animation="border" variant="danger" size="sm" /></div>
              ) : myRefunds.length === 0 ? (
                <div className="text-center py-4 bg-light rounded-3 text-muted">
                  <i className="bi bi-receipt-cutoff fs-2 d-block mb-1"></i>
                  <span>No refund requests found.</span>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {myRefunds.map((rr) => (
                    <div key={rr._id} className="p-3 border rounded-3 bg-white shadow-sm d-flex align-items-center justify-content-between">
                      <div>
                        <div className="fw-bold text-dark">Request ID: {rr._id}</div>
                        <div className="text-sm text-gray-600">Amount: <strong>${rr.requested_amount || rr.amount}</strong></div>
                        {rr.reason && <div className="text-xs text-muted mt-1">Reason: {rr.reason}</div>}
                      </div>
                      <Badge bg={rr.status === 'approved' ? 'success' : rr.status === 'rejected' ? 'danger' : 'warning'} className="px-3 py-2">
                        {rr.status || 'Pending'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Create Refund */}
          {refundTab === 'create' && (
            <Form onSubmit={handleCreateRefundSubmit} className="p-3 border rounded-3 bg-light">
              <h6 className="fw-bold text-dark mb-3">Submit New Refund Request</h6>
              {refundSuccess && <Alert variant="success" className="py-2 text-sm" onClose={() => setRefundSuccess(false)} dismissible>Refund request submitted successfully!</Alert>}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Transaction ID</Form.Label>
                <Form.Control
                  required
                  placeholder="Enter Transaction ID"
                  value={refundForm.transaction_id}
                  onChange={(e) => setRefundForm({ ...refundForm, transaction_id: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Refund Amount ($)</Form.Label>
                <Form.Control
                  required
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="e.g. 25.00"
                  value={refundForm.amount}
                  onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Reason for Refund</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Explain why you are requesting a refund..."
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                />
              </Form.Group>
              <Button type="submit" variant="danger" className="w-100 rounded-pill py-2 fw-bold" disabled={refundLoading}>
                {refundLoading ? <Spinner animation="border" size="sm" /> : 'Submit Refund Request'}
              </Button>
            </Form>
          )}

          {/* Tab 3: Teacher Incoming Requests */}
          {refundTab === 'teacher-incoming' && (
            <div>
              <h6 className="fw-bold text-dark mb-3">Incoming Student Refund Requests</h6>
              {refundLoading ? (
                <div className="text-center py-4"><Spinner animation="border" variant="danger" size="sm" /></div>
              ) : teacherRefunds.length === 0 ? (
                <div className="text-center py-4 bg-light rounded-3 text-muted">No pending refund requests to review.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {teacherRefunds.map((tr) => (
                    <div key={tr._id} className="p-3 border rounded-3 bg-white shadow-sm d-flex align-items-center justify-content-between">
                      <div>
                        <div className="fw-bold text-dark">Student: {tr.user?.full_name || 'Student'}</div>
                        <div className="text-sm text-gray-600">Amount: <strong>${tr.requested_amount || tr.amount}</strong></div>
                        {tr.reason && <div className="text-xs text-muted mt-1">Reason: {tr.reason}</div>}
                      </div>
                      <div className="d-flex gap-2">
                        <Button variant="success" size="sm" className="rounded-pill px-3" onClick={() => handleApproveRefund(tr._id)}>
                          Approve
                        </Button>
                        <Button variant="outline-danger" size="sm" className="rounded-pill px-3" onClick={() => handleRejectRefund(tr._id)}>
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Teacher Refunds History */}
          {refundTab === 'teacher-done' && (
            <div>
              <h6 className="fw-bold text-dark mb-3">Completed Refunds History</h6>
              {refundLoading ? (
                <div className="text-center py-4"><Spinner animation="border" variant="danger" size="sm" /></div>
              ) : teacherDoneRefunds.length === 0 ? (
                <div className="text-center py-4 bg-light rounded-3 text-muted">No completed refunds found.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {teacherDoneRefunds.map((dr) => (
                    <div key={dr._id} className="p-3 border rounded-3 bg-white shadow-sm d-flex align-items-center justify-content-between">
                      <div>
                        <div className="fw-bold text-dark">Refund #{dr._id}</div>
                        <div className="text-sm text-gray-600">Amount Refunded: <strong>${dr.requested_amount || dr.amount}</strong></div>
                      </div>
                      <Badge bg="success" className="px-3 py-2">Approved & Refunded</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Share YooSpeak Modal */}
      <Modal show={activeModal === 'share'} onHide={closeModal} centered>
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title><i className="bi bi-share-fill text-primary me-2"></i>Share YooSpeak</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom text-center">
          <div className="bg-light p-4 rounded-3 mb-3">
            <i className="bi bi-gift-fill fs-1 text-primary d-block mb-2"></i>
            <h5 className="fw-bold">Invite Friends & Earn Credits</h5>
            <p className="text-muted text-sm">Share YooSpeak with fellow language learners. Get $5 practice credit when they join!</p>
          </div>
          {copySuccess && (
            <Alert variant="success" className="py-2 text-sm">
              Link copied to clipboard!
            </Alert>
          )}
          <div className="input-group mb-3">
            <input type="text" readOnly className="form-control" value="https://yoospeak.com/app?ref=SHARE2026" />
            <Button variant="primary" onClick={handleCopyShareLink}>
              <i className="bi bi-clipboard me-1"></i>Copy
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SettingsPage;
